import { Component, EventEmitter, inject, Input, OnInit, Output, Signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { OidcSecurityService } from "angular-auth-oidc-client";

import { SidebarModule } from "primeng/sidebar";
import { DividerModule } from "primeng/divider";
import { InputSwitchModule } from "primeng/inputswitch";

import { ThemeService } from "@core/services/theme.service";
import { GlobalService } from "@shared/services";
import { SidebarMenuItem } from "@shared/models";
import { ButtonComponent } from "@shared/components/button/button.component";
import { MenuItemComponent } from "./components/menu-item/menu-item.component";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "app-sidebar",
  standalone: true,
  imports: [SidebarModule, MenuItemComponent, DividerModule, ButtonComponent, InputSwitchModule, FormsModule],
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent implements OnInit {
  globalService = inject(GlobalService);
  themeService = inject(ThemeService);
  oidcService = inject(OidcSecurityService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  menuItems: SidebarMenuItem[] = [
    {
      title: "Home",
      icon: "pi pi-home",
      route: "/home",
      isActive: false,
      unsavedContent: false,
    },
    {
      title: "Active Plan",
      icon: "pi pi-calendar",
      route: "/active",
      isActive: false,
      unsavedContent: this.globalService.activePlanUnsaved,
    },
    {
      title: "Standby Plan",
      icon: "pi pi-calendar",
      route: "/standby",
      isActive: false,
      unsavedContent: this.globalService.standbyPlanUnsaved,
    },
    {
      title: "History",
      icon: "pi pi-clock",
      route: "/history",
      isActive: false,
      unsavedContent: false,
    },
    {
      title: "Charts",
      icon: "pi pi-chart-bar",
      route: "/charts",
      isActive: false,
      unsavedContent: false,
    },
    {
      title: "Settings",
      icon: "pi pi-cog",
      route: "/settings",
      isActive: false,
      unsavedContent: false,
    },
    {
      title: "About",
      icon: "pi pi-info-circle",
      route: "/about",
      isActive: false,
      unsavedContent: false,
    }
  ];

  theme: boolean = false;

  ngOnInit(): void {
    this.route.url.subscribe(() => {
      const currentRoute = this.route.snapshot.routeConfig?.path;
      this.menuItems.forEach((item) => {
        item.isActive = item.route === `/${currentRoute}`;
      });
    });
    this.theme = this.setThemeSwitch();
  }

  setThemeSwitch(): boolean {
    const theme = localStorage.getItem("theme");
    if (theme) {
      return theme === "dark";
    }
    return false;
  }

  handleUnsavedContent(unsavedContent: boolean | Signal<boolean>): boolean {
    if (typeof unsavedContent === "boolean") {
      return unsavedContent;
    } else {
      return unsavedContent();
    }
  }

  onMenuItemClick(menuItem: SidebarMenuItem): void {
    this.router.navigate([menuItem.route]);
  }

  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  getSidebarStyles(): { height: string; marginTop: string } {
    return {
      height: `calc(100vh - ${this.globalService.navbarHeight()}px)`,
      marginTop: `${this.globalService.navbarHeight()}px`,
    };
  }

  getItemContainerHeight(): string {
    let offset = 245;
    if (this.globalService.isApaApp()) {
      offset = 150;
    }
    return `calc(100vh - ${this.globalService.navbarHeight()}px - ${offset}px)`;
  }

  toggleTheme(event: boolean): void {
    if (event) {
      this.themeService.applyTheme("dark");
    } else {
      this.themeService.applyTheme("light");
    }
  }

  signOut(): void {
    this.oidcService.logoff().subscribe();
  }
}
